/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useEffect, useState } from "react";

import { type SubmitHandler, Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";

import input from "../../../../assets/styles/components/input.module.css";
import select from "../../../../assets/styles/components/select.module.css";

import Button from "../../../../components/Button/Button";

import { toast } from "react-toastify";
import { getCustomer, registerOrEditClient } from "../../../../services/customers";
import styles from "./AddCustomer.module.css";

import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getCep, getCidade, getCNPJ, getEstado } from "../../../../services/address";
import type {
  CidadeAttributes,
  ClientFormData,
  EstadoAttributes,
  ICEPResult,
  ICNPJResult,
} from "../../../../types/customers";
import {
  maskCNPJ,
  maskCPF,
  maskPhone,
  validarCNPJ,
  validarCPF,
} from "../../../../utils/useMask";

interface prop {
  uid?: string;
  handleViewMode: (state: boolean) => void;
  isViewMode: boolean;
}

const FormCustomer = ({ uid, isViewMode, handleViewMode }: prop) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const inputClassName = isViewMode
    ? `${input.input} ${styles.inputViewMode}`
    : input.input;

  const selectClassName = isViewMode
    ? `${input.input} ${select.select} ${styles.inputViewMode}`
    : `${input.input} ${select.select}`;

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    control,
    trigger,
  } = useForm<ClientFormData>({
    defaultValues: { tipo_pessoa: "" as any, cpf_cnpj: "" },
  });

  const [cpfCnpjError, setCpfCnpjError] = useState("");
  const [telCelError, setTelCelError] = useState("");
  const [saldoAtual, setSaldoAtual] = useState(0);
  const [limiteCredito, setLimiteCredito] = useState(0);

  const [cep, setCep] = useState("");
  const [cpnj, setCpnj] = useState("");

  const isKindPerson = watch("tipo_pessoa");
  const id_estado = watch("id_estado");

  const [cepQuery, cnpjQuery, estadoQuery, cidadeQuery, customerQuery] =
    useQueries({
      queries: [
        {
          queryKey: ["getCep", cep],
          queryFn: () => getCep(cep || ""),
          enabled: !!cep && cep.replace(/\D/g, "").length === 8 && !isViewMode,
        },
        {
          queryKey: ["getCNPJ", cpnj],
          queryFn: () => getCNPJ(cpnj || ""),
          enabled: isKindPerson === "juridica" && !!cpnj && !isViewMode,
        },
        {
          queryKey: ["getEstado"],
          queryFn: () => getEstado(),
          refetchOnWindowFocus: false,
        },
        {
          queryKey: ["getCidade", id_estado],
          queryFn: () => getCidade(id_estado || ""),
          refetchOnWindowFocus: false,
          enabled: !!id_estado,
        },
        {
          queryKey: ["getCustomer", uid],
          queryFn: () => getCustomer(uid || ""),
          refetchOnWindowFocus: false,
          enabled: !!uid,
        },
      ],
    });

  useEffect(() => {
    if (customerQuery.data) {
      const customer = customerQuery.data as ClientFormData;

      Object.entries(customer).forEach(([key, value]) => {
        setValue(key as keyof ClientFormData, value);
      });

      if (customer.cpf_cnpj) {
        const cleanDoc = customer.cpf_cnpj.replace(/\D/g, "");
        const format =
          cleanDoc.length <= 11
            ? maskCPF(customer.cpf_cnpj)
            : maskCNPJ(customer.cpf_cnpj);
        setValue("tipo_pessoa", cleanDoc.length <= 11 ? "fisica" : "juridica");
        setValue("cpf_cnpj", format);
      } else {
        setValue("tipo_pessoa", "juridica");
      }

      if (customer.telefone) {
        const formattedTelCel = maskPhone(customer.telefone);
        const formattedWhatsapp = maskPhone(customer.whatsapp || "");
        setValue("telefone", formattedTelCel);
        setValue("whatsapp", formattedWhatsapp);
      }

      setSaldoAtual(customer.saldo_atual || 0);
      setLimiteCredito(customer.limite_credito || 0);
    }
  }, [customerQuery.data, setValue]);

  useEffect(() => {
    if (cepQuery.data && !isViewMode) {
      const { cep, bairro, complemento, localidade, logradouro, uf } =
        cepQuery.data as ICEPResult;
      setValue("cep", cep);
      setValue("bairro", bairro);
      setValue("complemento", complemento);
      setValue("cidade.municipio", localidade);
      setValue("endereco", logradouro);
      setValue("id_estado", uf);
    }
  }, [cepQuery.data, setValue, isViewMode]);

  useEffect(() => {
    if (cnpjQuery.data && !isViewMode) {
      const {
        cep,
        bairro,
        complemento,
        municipio,
        logradouro,
        uf,
        razao_social,
        nome_fantasia,
        ddd_telefone_1,
        email,
        numero,
      } = cnpjQuery.data as ICNPJResult;
      setValue("nome_fantasia", nome_fantasia || razao_social);
      setValue("telefone", maskPhone(ddd_telefone_1 || ""));
      setValue("email", email);
      setValue("numero", numero);
      setValue("cep", cep);
      setValue("bairro", bairro);
      setValue("complemento", complemento);
      setValue("id_estado", uf);
      setValue("endereco", logradouro);
      setValue("cidade.municipio", municipio);
    }
  }, [cnpjQuery.data, setValue, isViewMode]);

  useEffect(() => {
    if (cidadeQuery.data && cidadeQuery.data.length > 0) {
      const municipioNome = getValues("cidade.municipio");
      const idCidadeSalvo = customerQuery.data?.id_municipio;

      if (idCidadeSalvo) {
        setValue("id_municipio", Number(idCidadeSalvo));
      } else if (municipioNome) {
        const foundCity = cidadeQuery.data.find(
          (c: CidadeAttributes) =>
            c?.municipio?.toLowerCase() === municipioNome?.toLowerCase(),
        );
        if (foundCity) setValue("id_municipio", foundCity.id_municipio);
      }
    }
  }, [cidadeQuery.data, getValues, setValue]);

  const { mutate, isPending } = useMutation({
    mutationFn: registerOrEditClient,
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["clients"] });

      if (!uid && response.data) {
        navigate(`../edit/${response.data}`, {
          replace: true,
        });
      }

      handleViewMode(true);
    },
    onError: (error: any) => {
      if (error.message.includes("CPF/CNPJ")) setCpfCnpjError(error.message);
      else if (error.message.includes("Telefone"))
        setTelCelError(error.message);
      else {
        toast.error(error);
      }
    },
  });

  const onSubmit: SubmitHandler<ClientFormData> = async (data) => {
    setCpfCnpjError("");
    setTelCelError("");

    if (!data.tipo_pessoa) {
      toast.error("Tipo de pessoa é obrigatório");
      return;
    }

    if (!/^\p{L}/u.test(data.nome_fantasia)) {
      toast.error("Nome deve começar com uma letra");
      return;
    }

    const formattedData = {
      ...data,
      cpf_cnpj: data.cpf_cnpj?.replace(/\D/g, ""),
      telefone: data.telefone?.replace(/\D/g, ""),
      whatsapp: data.whatsapp?.replace(/\D/g, ""),
      cep: data.cep?.replace(/\D/g, ""),
      saldo_atual: saldoAtual,
      limite_credito: limiteCredito,
      id_municipio: data.id_municipio || null,
      id_estado: data.id_estado || null,
      uid_cliente: uid,
    };
    mutate(formattedData);
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const estadoId = e.target.value || null;
    setValue("id_estado", estadoId);
    setValue("id_municipio", null);
  };

  const handleCancelEdit = () => {
    handleViewMode(true);
  };

  const validateCpfCnpj = (v?: string) => {
    const val = (v || "").replace(/\D/g, "");
    if (!val) return true;
    if (isKindPerson === "juridica") {
      if (val.length !== 14 || !validarCNPJ(val)) return "CNPJ inválido";
    } else {
      if (val.length !== 11 || !validarCPF(val)) return "CPF inválido";
    }
    return true;
  };

  const labelS: React.CSSProperties = { fontSize: "0.72rem", color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" };
  const labelE: React.CSSProperties = { ...labelS, color: "#00b37e" };
  const inpE: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #00b37e", outline: "none", fontSize: "0.9rem", background: "#f0fff8", boxSizing: "border-box" as const };
  const inpV: React.CSSProperties = { ...inpE, border: "1px solid transparent", background: "transparent", pointerEvents: "none", color: "#222", fontWeight: 500 };
  const inpD: React.CSSProperties = { ...inpE, background: "#f0f0f0", color: "#888", cursor: "not-allowed" };
  const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px 20px", marginBottom: 20 };
  const divider: React.CSSProperties = { borderTop: "1px solid #eee", margin: "4px 0 20px" };
  const errSpan: React.CSSProperties = { color: "#e74c3c", fontSize: "0.78rem", marginTop: 3, display: "block" };

  const lbl = isViewMode ? labelS : labelE;
  const inp = (hasErr?: boolean) => isViewMode ? inpV : hasErr ? { ...inpE, border: "1px solid #e74c3c", background: "#fff5f5" } : inpE;
  const sel = isViewMode ? { ...inpV, appearance: "none" as const } : inpE;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      {/* Seção 1: Identificação */}
      <div style={grid}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={errors.nome_fantasia ? { ...lbl, color: "#e74c3c" } : lbl}>Nome *</label>
          <input style={inp(!!errors.nome_fantasia)} type="text" disabled={isViewMode}
            {...register("nome_fantasia", { required: "Obrigatório" })} placeholder={isViewMode ? "" : "Nome completo"} />
          {errors.nome_fantasia && <span style={errSpan}>⚠️ {errors.nome_fantasia.message}</span>}
        </div>
        <div>
          <label style={lbl}>Tipo de Pessoa</label>
          <select style={sel} disabled={isViewMode} {...register("tipo_pessoa")} required>
            <option value="" disabled>Selecione</option>
            <option value="fisica">Física</option>
            <option value="juridica">Jurídica</option>
          </select>
        </div>
        <div>
          <label style={lbl}>{isKindPerson === "juridica" ? "CNPJ" : "CPF"}</label>
          <Controller name="cpf_cnpj" control={control}
            rules={{ validate: (value) => validateCpfCnpj(value) || "CPF/CNPJ inválido" }}
            render={({ field: { onBlur, value, ref, onChange } }) => (
              <IMaskInput style={inp(!!errors.cpf_cnpj)} disabled={isViewMode}
                mask={isKindPerson === "juridica" ? "00.000.000/0000-00" : "000.000.000-00"}
                value={value as string} onAccept={(val) => onChange(val)}
                onBlur={() => { onBlur(); trigger("cpf_cnpj"); if (isKindPerson === "juridica") setCpnj((value || "").replace(/\D/g, "")); }}
                inputRef={ref as any} />
            )} />
          {errors.cpf_cnpj && <span style={errSpan}>⚠️ {errors.cpf_cnpj.message as string}</span>}
          {cpfCnpjError && <span style={errSpan}>{cpfCnpjError}</span>}
        </div>
        <div>
          <label style={lbl}>Inscrição Estadual</label>
          <input style={inp()} type="text" disabled={isViewMode} {...register("inscricao_estadual")} placeholder={isViewMode ? "" : "Inscrição Estadual"} />
        </div>
      </div>

      <div style={divider} />

      {/* Seção 2: Contato */}
      <div style={grid}>
        <div>
          <label style={lbl}>Telefone</label>
          <Controller name="telefone" control={control}
            render={({ field: { onChange, value, ref } }) => (
              <IMaskInput style={inp()} disabled={isViewMode}
                mask={[{ mask: "(00) 0000-0000" }, { mask: "(00) 0 0000-0000" }]}
                value={value as string} onAccept={(v) => { onChange(v); setTelCelError(""); }} inputRef={ref as any} />
            )} />
          {telCelError && <span style={errSpan}>{telCelError}</span>}
        </div>
        <div>
          <label style={lbl}>WhatsApp</label>
          <Controller name="whatsapp" control={control}
            render={({ field: { onChange, value, ref } }) => (
              <IMaskInput style={inp()} disabled={isViewMode}
                mask="(00) 0 0000-0000" value={value as string} onAccept={onChange} inputRef={ref as any} />
            )} />
        </div>
        <div>
          <label style={lbl}>Email</label>
          <input style={inp()} type="email" disabled={isViewMode} {...register("email")} placeholder={isViewMode ? "" : "E-mail"} />
        </div>
        {isKindPerson === "fisica" && (
          <div>
            <label style={lbl}>Data de Nascimento</label>
            <input style={inp()} type="date" disabled={isViewMode} {...register("nascimento")} />
          </div>
        )}
      </div>

      <div style={divider} />

      {/* Seção 3: Endereço */}
      <div style={grid}>
        <div>
          <label style={lbl}>CEP</label>
          <Controller name="cep" control={control}
            render={({ field: { onChange, value, ref } }) => (
              <IMaskInput style={inp()} disabled={isViewMode}
                mask="00000-000" value={value as string} onAccept={onChange}
                onBlur={() => typeof value === "string" && setCep(value)} inputRef={ref as any} />
            )} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={lbl}>Endereço</label>
          <input style={inp()} type="text" disabled={isViewMode} {...register("endereco")} />
        </div>
        <div>
          <label style={lbl}>Número</label>
          <input style={inp()} type="text" disabled={isViewMode} {...register("numero")} />
        </div>
        <div>
          <label style={lbl}>Complemento</label>
          <input style={inp()} type="text" disabled={isViewMode} {...register("complemento")} />
        </div>
        <div>
          <label style={lbl}>Bairro</label>
          <input style={inp()} type="text" disabled={isViewMode} {...register("bairro")} />
        </div>
        <div>
          <label style={lbl}>Estado</label>
          <select style={sel} disabled={isViewMode} {...register("id_estado")}
            onChange={(e) => { register("id_estado").onChange(e); handleStateChange(e); }}>
            <option value="">Selecione</option>
            {estadoQuery.data?.map((state: EstadoAttributes) => (
              <option key={state.id_estado} value={state.id_estado}>{state.estado}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Município</label>
          <select style={sel} {...register("id_municipio")}
            disabled={isViewMode || !cidadeQuery?.data?.length}
            onChange={(e) => setValue("id_municipio", Number(e.target.value))}>
            <option value="">Selecione</option>
            {cidadeQuery.data?.map((city: CidadeAttributes) => (
              <option key={city.id_municipio} value={city.id_municipio}>{city.municipio}</option>
            ))}
          </select>
        </div>
      </div>

      {!isViewMode && (
        <div className={styles.buttonSave}>
          <Button variant="contain" type="submit" disabled={isPending}>
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
          {!isViewMode && uid && (
            <Button variant="outlined" className={styles.cancelButton} onClick={handleCancelEdit} type="button" disabled={isPending}>
              Cancelar
            </Button>
          )}
        </div>
      )}
    </form>
  );
};

export default FormCustomer;